import React from 'react';
import { clsx } from 'clsx';
import type { FreshnessStatus } from 'shared/src/trpc-types/freshness-types';
import {
  NA_STYLE,
  SUMMARY_KEY_TO_STATUS,
  formatBadgeLabel,
  freshnessStatusStyle
} from './freshness-utils';
import type { FreshnessBadgeUnit, StatusCounts, SummaryPillKey } from './freshness-utils';

export function StatusBadge({
  status,
  amountBehind,
  unit = 'd',
  prefix,
  pulse = true
}: {
  status: FreshnessStatus;
  amountBehind: number;
  unit?: FreshnessBadgeUnit;
  prefix?: string;
  pulse?: boolean;
}) {
  const styles = freshnessStatusStyle(status);
  const label = prefix
    ? `${prefix}: ${formatBadgeLabel(status, amountBehind, unit)}`
    : formatBadgeLabel(status, amountBehind, unit);

  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        styles.bg,
        styles.text
      )}>
      <span
        className={clsx(
          'h-2 w-2 rounded-full',
          styles.dot,
          status === 'very_stale' && pulse && 'animate-pulse'
        )}
        aria-hidden="true"
      />
      <span>{label}</span>
    </div>
  );
}

export function NaBadge({ prefix }: { prefix: string }) {
  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        NA_STYLE.bg,
        NA_STYLE.text
      )}>
      <span>
        {prefix}: {NA_STYLE.label}
      </span>
    </div>
  );
}

export function SummaryPill({
  status,
  count,
  'aria-hidden': ariaHidden
}: {
  status: SummaryPillKey;
  count: number;
  'aria-hidden'?: boolean;
}) {
  const freshnessStatus = SUMMARY_KEY_TO_STATUS[status];
  const styles = freshnessStatusStyle(freshnessStatus);
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        styles.bg,
        styles.text
      )}
      aria-hidden={ariaHidden}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', styles.dot)} />
      {count}
    </span>
  );
}

const SUMMARY_PILL_KEYS: SummaryPillKey[] = ['fresh', 'stale', 'critical', 'idle', 'noData'];

export function SummaryPills({
  counts,
  ariaHidden
}: {
  counts: StatusCounts;
  ariaHidden?: boolean;
}) {
  return (
    <>
      {SUMMARY_PILL_KEYS.map(key =>
        counts[key] > 0 ? (
          <SummaryPill key={key} status={key} count={counts[key]} aria-hidden={ariaHidden} />
        ) : null
      )}
    </>
  );
}
